import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import localSettings from "@app/config/LocalSettings";
import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  applyEfficiencyTrackerDrillDownListViewSettings,
  exportEfficiencyTrackerCardDrillDownData,
  fetchEfficiencyTrackerCardData,
  fetchEfficiencyTrackerCardDrillDownData,
  setEfficiencyTrackerDrillDownStats,
  showEfficiencyTrackerDrillDown,
} from "@app/scene/dashboard/store/cards/efficiencyTracker/actions";
import {
  getEfficiencyTrackerDrillDownConfiguration,
  getEfficiencyTrackerDrillDownTotalFilteredRecords,
  getEfficiencyTrackerDrillDownViewState,
} from "@app/scene/dashboard/store/cards/efficiencyTracker/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import expandColumns from "@app/scene/dashboard/utils/expandColumns";
import { callApi } from "@app/store/api/callApi";
import { fetchEfficiencyTrackerCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import buildEfficiencyDrillDownFilter from "@app/store/dashboard/efficiencyTracker/buildEfficiencyDrillDownFilter";
import fetchEfficiencyDrillDownStats, {
  EfficiencyDrillDownStats,
} from "@app/store/dashboard/efficiencyTracker/fetchEfficiencyDrillDownStats";
import getEfficiencyDrillDownColumnConfig from "@app/store/dashboard/efficiencyTracker/getEfficiencyDrillDownColumnConfig";
import getEfficiencyDrillDownViewStateKey from "@app/store/dashboard/efficiencyTracker/getEfficiencyDrillDownViewStateKey";
import getEfficiencyMetricDescriptor from "@app/store/dashboard/efficiencyTracker/getEfficiencyMetricDescriptor";
import getSelectedFunnelIds from "@app/store/dashboard/efficiencyTracker/getSelectedFunnelIds";
import getWonStageIds from "@app/store/dashboard/efficiencyTracker/getWonStageIds";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import EfficiencyTrackerCardConfiguration from "@app/types/dashboard/cards/efficiencyTracker/EfficiencyTrackerCardConfiguration";
import ActivityFieldName from "@app/util/fieldModel/ActivityFieldName";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import DealFieldName from "@app/util/fieldModel/DealFieldName";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { createEmptyFilterModel } from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

function* onFetchEfficiencyTrackerCardData({
  payload,
}: ReturnType<typeof fetchEfficiencyTrackerCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;

  try {
    yield call(fetchEfficiencyTrackerCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

export function* onOpenDrillDown({
  payload,
}: ReturnType<typeof showEfficiencyTrackerDrillDown.request>) {
  const { configuration, customDateRange, scope } = payload;
  const { criteria, dateRange, extraFilters } = configuration;

  const criteriaFieldModel = getFieldModelByEntityType(criteria.entityType ?? EntityType.ACTIVITY);
  const descriptor = getEfficiencyMetricDescriptor(criteria, criteriaFieldModel);
  const entityType = descriptor.entityType;
  const fieldModel = getFieldModelByEntityType(entityType);

  const { columns, sortColumn } = getEfficiencyDrillDownColumnConfig(criteria, entityType);

  const drillDownViewState: DrillDownViewState = yield select(
    getEfficiencyTrackerDrillDownViewState
  );
  const viewStateKey = getEfficiencyDrillDownViewStateKey(criteria, entityType);
  const viewState = localSettings.getViewSettings(viewStateKey, fieldModel, {
    ...drillDownViewState,
    columns: getDefaultDrillDownColumns(columns, fieldModel),
    sort: [{ field: fieldModel.getByName(sortColumn)!, order: SortOrder.DESC }],
  });

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const range = customDateRange ?? getDatesRange(dateRange.range, dateRange.subRange);
  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const userIds: User["id"][] = scope?.userId
    ? [scope.userId]
    : (scopeTeam?.users.map(({ id }) => id) ?? []);

  let wonStageIds: Stage["id"][] = [];
  if (criteria.fieldType === "wonRevenue") {
    const funnelStages: Record<number, Stage[]> = yield select(getFunnelStages);
    const funnels: Funnel[] = yield select(getFunnels);
    wonStageIds = getWonStageIds(
      funnelStages,
      getSelectedFunnelIds(
        extraFilters,
        funnels.map(({ id }) => id)
      )
    );
  }

  const dateFieldName =
    criteria.fieldType === "facetime"
      ? ActivityFieldName.COMPLETED_AT
      : criteria.fieldType === "wonRevenue"
        ? DealFieldName.CLOSING_DATE
        : CommonFieldName.CREATED_AT;

  const filter = buildEfficiencyDrillDownFilter({
    criteria,
    dateFieldName,
    entityType,
    extraFilters,
    fieldModel,
    range,
    scope,
    userIds,
    wonStageIds,
  });
  viewState.filter = filter;

  // Publish the view state (with columns) BEFORE the async stats fetch. Otherwise the modal,
  // which opens on the request action, mounts the grid on the empty initial view state and
  // onFetchDrillDownData persists those empty columns — corrupting the saved per-metric state.
  yield put(showEfficiencyTrackerDrillDown.success({ viewState }));

  const stats: EfficiencyDrillDownStats = yield call(fetchEfficiencyDrillDownStats, {
    columns: viewState.columns,
    descriptor,
    fieldModel,
    filterModel: filter,
    range,
    userIds,
  });

  yield put(setEfficiencyTrackerDrillDownStats(stats));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchEfficiencyTrackerCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: activities logged card, onFetchDrillDownData", payload);
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    const configuration: EfficiencyTrackerCardConfiguration = yield select(
      getEfficiencyTrackerDrillDownConfiguration
    );
    const { criteria } = configuration;
    const criteriaFieldModel = getFieldModelByEntityType(
      criteria.entityType ?? EntityType.ACTIVITY
    );
    const descriptor = getEfficiencyMetricDescriptor(criteria, criteriaFieldModel);
    const entityType = descriptor.entityType;
    const fieldModel = getFieldModelByEntityType(entityType);

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyEfficiencyTrackerDrillDownListViewSettings(payload.request));
    }

    const drillDownViewState: DrillDownViewState = yield select(
      getEfficiencyTrackerDrillDownViewState
    );

    // Never persist an un-initialized (column-less) view state — guards against saving the
    // empty initial state if the grid's first fetch races ahead of the open saga's success.
    if (!payload.fetchOnlyWithoutFilters && drillDownViewState.columns.length) {
      localSettings.setViewSettings(
        drillDownViewState,
        getEfficiencyDrillDownViewStateKey(criteria, entityType)
      );
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          fieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const response: ListResponse<AnyEntity> = yield callApi(
      descriptor.fetchMethod,
      orgId,
      requestPayload
    );
    payload.dataCallback?.(response);
    yield put(
      fetchEfficiencyTrackerCardDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchEfficiencyTrackerCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onExport() {
  const configuration: EfficiencyTrackerCardConfiguration = yield select(
    getEfficiencyTrackerDrillDownConfiguration
  );
  const { criteria } = configuration;
  const criteriaFieldModel = getFieldModelByEntityType(criteria.entityType ?? EntityType.ACTIVITY);
  const { entityType } = getEfficiencyMetricDescriptor(criteria, criteriaFieldModel);

  const drillDownViewState: DrillDownViewState = yield select(
    getEfficiencyTrackerDrillDownViewState
  );
  const total: number = yield select(getEfficiencyTrackerDrillDownTotalFilteredRecords);

  yield put(
    exportEntities.request({
      entityType,
      total,
      viewState: expandColumns(drillDownViewState),
    })
  );
}

export function* efficiencyTrackerSagas() {
  // we use takeEvery because there might be several cards of such type on board
  // however, it would be more optimal to use takeLatest, but also filter by card ids
  yield takeEvery(fetchEfficiencyTrackerCardData, onFetchEfficiencyTrackerCardData);
  yield takeLatest(showEfficiencyTrackerDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchEfficiencyTrackerCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportEfficiencyTrackerCardDrillDownData, onExport);
}
